// Helper function to parse duration string to minutes
const parseDuration = (duration) => {
  const match = duration.match(/(\d+)h\s*(\d+)?m?/);
  if (match) {
    const hours = parseInt(match[1]);
    const minutes = match[2] ? parseInt(match[2]) : 0;
    return hours * 60 + minutes;
  }
  return 0;
};

// Helper function to parse time string to minutes since midnight
const parseTimeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Calculate a quality score for a flight based on multiple factors:
 * - Direct flights get +50 points
 * - Premium airlines (BA, LH, AF, KL, LX) get +30 points
 * - Each amenity gets +5 points
 * - Lower price gets higher score (inverted)
 * - Shorter duration gets higher score (inverted)
 */
const calculateQualityScore = (flight) => {
  let score = 0;
  
  const outbound = flight.outbound;
  
  // Direct flights get higher score
  if (outbound.stops === 0) score += 50;
  
  // Premium airlines get higher score
  const premiumAirlines = ['BA', 'LH', 'AF', 'KL', 'LX'];
  if (premiumAirlines.includes(outbound.airline)) score += 30;
  
  // More amenities get higher score
  if (outbound.amenities) {
    score += outbound.amenities.length * 5;
  }
  
  // Lower price gets higher score (inverted)
  const maxPrice = 1000; // Assume max price for normalization
  score += Math.max(0, (maxPrice - flight.totalPrice) / 10);
  
  // Shorter duration gets higher score (inverted)
  const durationMinutes = parseDuration(outbound.duration);
  score += Math.max(0, (300 - durationMinutes) / 2); // 300 minutes = 5 hours as baseline
  
  return score;
};

/**
 * Sort flights by cheapest price (total price for round trips)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with cheapest first
 */
export const sortByCheapest = (flights) => {
  return [...flights].sort((a, b) => {
    return a.totalPrice - b.totalPrice;
  });
};

/**
 * Sort flights by best quality score (considers price, duration, amenities, airline quality)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with best quality first
 */
export const sortByBest = (flights) => {
  return [...flights].sort((a, b) => {
    const scoreA = calculateQualityScore(a);
    const scoreB = calculateQualityScore(b);
    return scoreB - scoreA; // Higher score first
  });
};

/**
 * Sort flights by quickest duration (outbound flight duration)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with shortest duration first
 */
export const sortByQuickest = (flights) => {
  return [...flights].sort((a, b) => {
    const durationA = parseDuration(a.outbound.duration);
    const durationB = parseDuration(b.outbound.duration);
    return durationA - durationB;
  });
};

/**
 * Sort flights by earliest take-off time (SFO)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with earliest take-off first
 */
export const sortByEarliestTakeoff = (flights) => {
  return [...flights].sort((a, b) => {
    const timeA = parseTimeToMinutes(a.outbound.departure.time);
    const timeB = parseTimeToMinutes(b.outbound.departure.time);
    return timeA - timeB;
  });
};

/**
 * Sort flights by latest take-off time (SFO)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with latest take-off first
 */
export const sortByLatestTakeoff = (flights) => {
  return [...flights].sort((a, b) => {
    const timeA = parseTimeToMinutes(a.outbound.departure.time);
    const timeB = parseTimeToMinutes(b.outbound.departure.time);
    return timeB - timeA; // Reverse order for latest first
  });
};

/**
 * Sort flights by earliest landing time (LAX)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with earliest landing first
 */
export const sortByEarliestLanding = (flights) => {
  return [...flights].sort((a, b) => {
    const timeA = parseTimeToMinutes(a.outbound.arrival.time);
    const timeB = parseTimeToMinutes(b.outbound.arrival.time);
    return timeA - timeB;
  });
};

/**
 * Sort flights by latest landing time (LAX)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with latest landing first
 */
export const sortByLatestLanding = (flights) => {
  return [...flights].sort((a, b) => {
    const timeA = parseTimeToMinutes(a.outbound.arrival.time);
    const timeB = parseTimeToMinutes(b.outbound.arrival.time);
    return timeB - timeA; // Reverse order for latest first
  });
};

/**
 * Sort flights by highest price
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with highest price first
 */
export const sortByHighestPrice = (flights) => {
  return [...flights].sort((a, b) => {
    return b.totalPrice - a.totalPrice; // Reverse order for highest first
  });
};

/**
 * Sort flights by slowest duration (longest duration first)
 * @param {Array} flights - Array of flight results
 * @returns {Array} Sorted flights with longest duration first
 */
export const sortBySlowest = (flights) => {
  return [...flights].sort((a, b) => {
    const durationA = parseDuration(a.outbound.duration);
    const durationB = parseDuration(b.outbound.duration);
    return durationB - durationA; // Reverse order for longest first
  });
};

/**
 * Main sorting function that routes to the appropriate sort method
 * @param {Array} flights - Array of flight results
 * @param {string} sortType - Sort type: 'cheapest', 'best', 'quickest', 'earliest-takeoff-sfo', 'latest-takeoff-sfo', 'earliest-landing-lax', 'latest-landing-lax', 'highest-price', 'slowest'
 * @returns {Array} Sorted flights
 */
export const sortFlights = (flights, sortType) => {
  switch (sortType) {
    case 'cheapest':
      return sortByCheapest(flights);
    case 'best':
      return sortByBest(flights);
    case 'quickest':
      return sortByQuickest(flights);
    case 'earliest-takeoff-sfo':
      return sortByEarliestTakeoff(flights);
    case 'latest-takeoff-sfo':
      return sortByLatestTakeoff(flights);
    case 'earliest-landing-lax':
      return sortByEarliestLanding(flights);
    case 'latest-landing-lax':
      return sortByLatestLanding(flights);
    case 'highest-price':
      return sortByHighestPrice(flights);
    case 'slowest':
      return sortBySlowest(flights);
    default:
      return sortByBest(flights); // Default to best
  }
}; 